<template>
  <div
    class="tw-rounded-sm tw-p-5 tw-bg-primary-grey-50 tw-h-full"
    :data-test-id="`${tier}-${dataTestIdType}-card`"
  >
    <div class="tw-flex tw-justify-between tw-gap-1 tw-mb-2">
      <div class="tw-flex tw-gap-2 tw-items-center">
        <div
          v-tippy="{
            content: tooltipText,
            placement: 'top',
          }"
          class="tw-w-6 tw-h-6 tw-p-0.5 tw-bg-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-place-self-center"
        >
          <component :is="icon" width="16" height="16" />
        </div>
        <div class="tw-font-semibold tw-text-lg">{{ $t(title) }}</div>
      </div>
      <a
        target="_blank"
        :href="documentationLink"
        :data-test-id="`${tier}-${dataTestIdType}-card-learn-more-external-link`"
      >
        <cl-button
          variant="secondary"
          size="small"
          class="tw-text-nowrap tw-self-center"
          :data-test-id="`${tier}-${dataTestIdType}-card-learn-more-button`"
        >
          <div class="tw-flex tw-gap-1 tw-items-center">
            {{ $t("learn-more") }}
            <cb-icon-external-link :width="16" />
          </div>
        </cl-button>
      </a>
    </div>
    <div>{{ $t(text) }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { EXTERNAL_DOCS_LINKS } from "@/constants/externalDocsLinks";
import { SERVICE_ICON } from "@/utils/services";
import { useIsMspRoute } from "@/composables/useIsMspRoute.ts";
import { CUSTOMER, MSP } from "@/constants";

type EmptyCard = "EMAIL_SECURITY" | "PHISHING_SIMULATION" | "SAT";

const props = defineProps<{ type: EmptyCard }>();
const { t } = useI18n();

const { isMspRoute } = useIsMspRoute();
const tier = isMspRoute.value ? MSP : CUSTOMER;

const title = computed(
  () =>
    ({
      EMAIL_SECURITY: "email-security-solution",
      PHISHING_SIMULATION: "phishing-simulation-label",
      SAT: "sat-label",
    })[props.type],
);

const icon = computed(
  () =>
    ({
      EMAIL_SECURITY: SERVICE_ICON.PHISH_SERVICE,
      PHISHING_SIMULATION: SERVICE_ICON.PHISHING_SIMULATION,
      SAT: SERVICE_ICON.SAT_SERVICE,
    })[props.type],
);

const text = computed(
  () =>
    ({
      EMAIL_SECURITY: "email-security",
      PHISHING_SIMULATION: "phishing-simulation-value",
      SAT: "sat-value",
    })[props.type],
);

const documentationLink = computed(
  () =>
    ({
      EMAIL_SECURITY: EXTERNAL_DOCS_LINKS.EMAIL_SECURITY,
      PHISHING_SIMULATION: EXTERNAL_DOCS_LINKS.TITANHQ_CONTACT_PAGE,
      SAT: EXTERNAL_DOCS_LINKS.TITANHQ_CONTACT_PAGE,
    })[props.type],
);

const tooltipText = computed(
  () =>
    ({
      EMAIL_SECURITY: t("email-security-solution"),
      PHISHING_SIMULATION: t("phishing-simulation-label"),
      SAT: t("sat-label"),
    })[props.type],
);

const dataTestIdType = computed(
  () =>
    ({
      EMAIL_SECURITY: "email-security-solution",
      PHISHING_SIMULATION: "phishing-simulation-solution",
      SAT: "sat-solution",
    })[props.type],
);
</script>
